<script setup lang="ts">
import {
  CardComponent,
  CardNumber,
  CardExpiry,
  CardCvv,
} from '@chargebee/chargebee-js-vue-wrapper';
import BaseButton from '@components/quiz_v2/BaseButton/BaseButton.vue';
import BaseCheck from '@components/quiz_v2/BaseCheck/BaseCheck.vue';
import BaseSpinner from '@components/quiz_v2/BaseSpinner/BaseSpinner.vue';
import { StepCheckout } from '@models/quiz';
import { PaymentMethod } from '@models/user';
import { useCheckoutStore, PaymentMethodTypes } from '@stores/checkout/checkoutStore';
import { format, parseISO, isAfter } from 'date-fns';
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';
import IconApplePay from '../../assets/icons/apple-pay.svg';
import IconArrowLeft from '../../assets/icons/arrow-left.svg';
import IconArrowRight from '../../assets/icons/arrow-right.svg';
import IconLock from '../../assets/icons/lock.svg';
import IconPlus from '../../assets/icons/plus.svg';
import imgCards from '../../assets/img/checkout-cart-cards.png';
import { useWeightLossPlans } from '../../composables/useWeightLossPlans';
import {
  ChargebeeChangeEvent,
  ChargebeeFieldType,
  ChargebeeFieldError,
} from './types';

defineProps<{
  content: StepCheckout;
}>();

const emit = defineEmits<{
  'next': [];
  'back': [];
}>();

const checkoutStore = useCheckoutStore();
const { activePeriodOption } = useWeightLossPlans();

const {
  applePayPaymentIntentId,
  paymentMethodType,
  isApplePayCompatible,
  isApplePayInited,
  createApplePaymentIntentLoading,
  checkoutForm,
  checkout$,
  paymentMethods,
  isNewPaymentMethod,
  cardComponent,
  buyNowLoading,
} = storeToRefs(checkoutStore);

const { deletePaymentMethod, buyNow } = checkoutStore;

const cardSettings = {
  icon: false,
  styles: {
    base: {
      fontFamily: 'BR Firma, sans-serif',
      fontSize: '18px',
      fontStyle: 'normal',
      color: '#111111',

      '::placeholder': {
        color: '#AAA092',
      },
    },
  },
  classes: {
    focus: 'focus',
    invalid: 'invalid',
  },
  fonts: [
    {
      src: 'https://jrnys.com/assets/fonts/BRFirma-Regular.woff2',
      fontFamily: 'BR Firma',
      fontWeight: '400',
      fontStyle: 'normal',
    },
  ],
};

const cardErrors = ref<Partial<Record<ChargebeeFieldType, ChargebeeFieldError>>>({});
const cardErrorMessage = ref<string>();

const onCardChange = (status: ChargebeeChangeEvent) => {
  cardErrors.value = { ...cardErrors.value, [status.field]: status.error };
  const { message } = Object.values(cardErrors.value).filter((message) => !!message).pop() || {};
  cardErrorMessage.value = message;
};

const paymentMethodList = ref(paymentMethods.value);

watch(paymentMethods, (newValue) => {
  paymentMethodList.value = newValue;
});

const addNewPaymentMethod = () => {
  isNewPaymentMethod.value = true;
  checkoutForm.value.paymentSourceId = null;
};

const selectPaymentMethod = (method: PaymentMethod) => {
  if (isExpired(method.card.expirationDate)) return;
  checkoutForm.value.paymentSourceId = method.externalRef;
  isNewPaymentMethod.value = false;
};

const deleteMethod = (paymentSourceId: string) => {
  if (!confirm('Are you sure you want to delete payment method')) {
    return;
  }
  paymentMethodList.value = paymentMethodList.value.filter((method) => {
    return method.externalRef !== paymentSourceId;
  });
  deletePaymentMethod(paymentSourceId);
};

const isExpired = (expirationDate: string): boolean => {
  return isAfter(new Date(), parseISO(expirationDate));
};
</script>

<template>
  <div class="flex flex-col items-center">
    <div class="checkout-slide-left w-full max-w-[530px]">
      <div
        v-if="paymentMethods.length !== 0"
        :class="{ 'validation-error': checkout$.paymentSourceId.$error }"
      >
        <h2 class="text-24 font-medium">
          Choose a saved payment option:
        </h2>

        <p
          v-if="activePeriodOption?.paymentDescription"
          class="mt-12 text-14"
        >
          {{ activePeriodOption.paymentDescription }}
        </p>

        <ul class="mt-32 flex flex-col gap-12">
          <li
            v-for="method in paymentMethodList"
            :key="method.externalRef"
          >
            <button
              class="flex h-[60px] w-full cursor-pointer items-center gap-8 rounded-[5px] border border-primary-500 px-20 text-left md:gap-20"
              :class="{
                'bg-primary-500 text-white': checkoutForm.paymentSourceId === method.externalRef,
              }"
              type="button"
              @click="selectPaymentMethod(method)"
            >
              <span
                class="text-16 font-bold"
                :class="{'text-primary-300 line-through': isExpired(method.card.expirationDate)}"
              >
                Visa ending {{ method.card.ending }}
              </span>
              <span
                v-if="!isExpired(method.card.expirationDate)"
                class="ml-auto text-12"
              >
                Expires {{ format(parseISO(method.card.expirationDate), 'MM/yy') }}
              </span>
              <span
                v-else
                class="ml-auto text-12 text-error-500"
              >
                Expired {{ format(parseISO(method.card.expirationDate), 'MM/yy') }}
              </span>

              <span
                class="p-6 text-12 font-bold hover:text-error-500"
                :class="{
                  'text-primary-500': checkoutForm.paymentSourceId !== method.externalRef,
                }"
                @click.stop="deleteMethod(method.externalRef)"
              >
                Remove
              </span>
            </button>
          </li>
        </ul>

        <BaseButton
          v-if="!isNewPaymentMethod"
          class="mt-28 w-full"
          data-testpl="checkout-add-payment-btn"
          @click="addNewPaymentMethod"
        >
          Add new payment method

          <template #icon-right>
            <IconPlus />
          </template>
        </BaseButton>

        <p
          v-if="checkout$.paymentSourceId.$error"
          class="mt-8 text-14 text-error-500"
        >
          {{ checkout$.paymentSourceId.$errors[0]?.$message }}
        </p>
      </div>

      <div
        v-if="isNewPaymentMethod"
        class="mt-40"
        :class="{ 'validation-error': cardErrorMessage }"
      >
        <h2 class="text-24 font-medium">
          Add a payment option
        </h2>

        <p
          v-if="activePeriodOption?.paymentDescription && paymentMethods.length === 0"
          class="mt-12 text-14"
        >
          {{ activePeriodOption.paymentDescription }}
        </p>

        <div class="mt-32 flex items-center justify-between gap-16">
          <div class="flex items-center gap-10">
            <BaseCheck
              v-model="paymentMethodType"
              :value="PaymentMethodTypes.Card"
              type="radio"
            >
              <div class="ml-10">
                Credit or Debit Card
              </div>
            </BaseCheck>
          </div>
          <img
            class="w-[143px]"
            :src="imgCards"
            alt=""
          >
        </div>

        <div v-show="paymentMethodType === PaymentMethodTypes.Card">
          <CardComponent
            ref="cardComponent"
            class="mt-28 flex flex-wrap gap-16"
            v-bind="cardSettings"
            data-testpl="checkout-cart-card-component"
            @change="onCardChange"
          >
            <div class="w-full">
              <CardNumber
                class="checkout-input"
                placeholder="Credit Card Number"
              />
            </div>
            <div class="grow">
              <CardExpiry
                class="checkout-input"
                placeholder="Expiry"
              />
            </div>
            <div class="w-[170px]">
              <CardCvv
                class="checkout-input"
                placeholder="CVV"
              />
            </div>
          </CardComponent>

          <p
            v-if="cardErrorMessage"
            class="mt-8 text-14 text-error-500"
          >
            {{ cardErrorMessage }}
          </p>
        </div>

        <div
          v-if="isApplePayCompatible"
          class="mt-32 flex items-center justify-between gap-16"
        >
          <BaseCheck
            v-model="paymentMethodType"
            :value="PaymentMethodTypes.ApplePay"
            type="radio"
          >
            <div class="ml-10">
              Purchase with Apple Pay
            </div>
          </BaseCheck>
          <IconApplePay class="ml-auto h-20 w-32" />
        </div>

        <div v-show="paymentMethodType === PaymentMethodTypes.ApplePay">
          <div
            v-if="createApplePaymentIntentLoading || !isApplePayInited"
            class="flex h-48 items-center justify-center"
          >
            <BaseSpinner class="size-20" />
          </div>
          <div
            id="apple-pay-button"
            class="mt-20"
          />
        </div>
      </div>

      <div class="mt-40 grid items-center gap-x-20 gap-y-16 md:mt-60 md:grid-cols-[185px,1fr]">
        <BaseButton
          color="black"
          data-testpl="checkout-payment-continue-btn"
          animated
          :loading="buyNowLoading || createApplePaymentIntentLoading"
          :disabled="paymentMethodType === PaymentMethodTypes.ApplePay && !applePayPaymentIntentId"
          @click="buyNow"
        >
          <template v-if="paymentMethodType === PaymentMethodTypes.ApplePay">
            <template v-if="!applePayPaymentIntentId">
              Awaiting Apple Pay payment
            </template>
            <template v-else>
              Pay Now and Submit to Provider
            </template>
          </template>
          <template v-else>
            Pay Now and Submit to Provider
          </template>

          <template #icon-right>
            <IconArrowRight />
          </template>
        </BaseButton>

        <BaseButton
          class="md:order-first"
          theme="outlined"
          data-testpl="checkout-payment-back-btn"
          animated
          @click="emit('back')"
        >
          Back

          <template #icon-left>
            <IconArrowLeft />
          </template>
        </BaseButton>
      </div>

      <div
        v-if="activePeriodOption?.agreements"
        class="mt-60 flex flex-col gap-10 text-center text-12 text-black opacity-50"
        v-html="activePeriodOption.agreements"
      />

      <div class="mt-20 flex items-center justify-center gap-12 text-14 font-bold uppercase text-black opacity-50 md:mt-24">
        <IconLock />
        <span>256-Bit TLS Security</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.checkout-input {
  @apply
    relative
    flex
    h-[49px]
    w-full
    items-center
    rounded-[5px]
    px-16
    text-16
    text-black
    outline
    outline-2
    outline-transparent
    -outline-offset-1
    border
    border-primary-200
    bg-white
    transition-all
    duration-200;

  &.focus {
    @apply
      outline-primary-500;
  }

  &.invalid {
    @apply
      outline-error-500;
  }
}
</style>
